import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Grid,
  TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '../../utils/api-error';

interface PduOutletEditProps {
  handleClose: () => void;
  pduId: string;
  outletId: string;
  outletPortName: string;
  deviceName: string;
  onSuccess?: () => void;
}

export const PduOutletEdit = ({ 
  handleClose, 
  pduId, 
  outletId, 
  outletPortName,
  deviceName,
  onSuccess,
}: PduOutletEditProps) => {
  const api = useApi();
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [portName, setPortName] = useState<string>(outletPortName);
  const [description, setDescription] = useState<string>(deviceName || '');

  useEffect(() => {
    setPortName(outletPortName);
    setDescription(deviceName || '');
  }, [outletPortName, deviceName]);

  const hasChanges = useMemo(() => {
    return description.trim() !== (deviceName || '').trim();
  }, [description, deviceName]);

  const editOutletMutation = useMutation(api.editPduOutletDeviceNameApiV1EditPduOutletDeviceNamePost, {
    onSuccess: () => {
      toast.success('Outlet device name updated successfully.');
      setLoadingModalOpen(false);
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: any) => {
      apiError(error);
      setLoadingModalOpen(false);
    },
  });

  const handleSubmit = () => {
    if (!description.trim()) {
      toast.error('Please enter a description.');
      return;
    }

    setLoadingModalOpen(true);
    editOutletMutation.mutate({
      pdu_id: pduId,
      outlet_id: parseInt(outletId),
      device_name: description.trim(),
    });
  };

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="port_name"
            label="Port Name"
            variant="outlined"
            value={portName}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="device_name"
            label="Description (Device Name)"
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autoFocus
            helperText="Enter the device name or description for this outlet"
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={handleClose} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!hasChanges}>
              Save Changes
            </Button>
          </Box>
        </Grid>
      </Grid>
      {loadingModalOpen && <PageLoading content="Updating outlet..." />}
    </Box>
  );
};
